import { addDaysIso, CRM_KEYS, daysBetween, nowIso, readCrmJson, writeCrmJson } from './storage'
import { opportunityService } from './OpportunityService'
import type { Opportunity, Reminder, ReminderLevel } from './types'

/**
 * ReminderService — idle-day alerts (3 / 5 / 7 days).
 */
export class ReminderService {
  list(opportunityId?: string): Reminder[] {
    const all = readCrmJson<Reminder[]>(CRM_KEYS.reminders, 'reminders', [])
    if (!opportunityId) return all
    return all.filter((r) => r.opportunityId === opportunityId)
  }

  listPending(): Reminder[] {
    return this.list().filter((r) => r.status === 'PENDING' || r.status === 'SHOWN')
  }

  /**
   * Regenerates reminders for open opportunities based on days idle.
   */
  syncFromOpportunities(opportunities?: Opportunity[]): Reminder[] {
    const open = (opportunities ?? opportunityService.list()).filter(
      (o) => !['APPROVED', 'LOST', 'CANCELLED'].includes(o.stage),
    )
    const existing = this.list().filter((r) => r.status === 'DONE' || r.status === 'DISMISSED')
    const generated: Reminder[] = []

    for (const opportunity of open) {
      const days = daysBetween(opportunity.lastContact ?? opportunity.updatedAt)
      const level = this.levelForDays(days)
      if (!level) continue

      generated.push({
        id: crypto.randomUUID(),
        opportunityId: opportunity.id,
        date: nowIso(),
        message: this.messageFor(opportunity, days, level),
        status: 'PENDING',
        level,
        daysIdle: days,
        createdAt: nowIso(),
      })
    }

    const next = [...generated, ...existing].slice(0, 300)
    writeCrmJson(CRM_KEYS.reminders, 'reminders', next)
    return generated
  }

  markShown(id: string): Reminder {
    return this.patch(id, { status: 'SHOWN' })
  }

  markDone(id: string): Reminder {
    return this.patch(id, { status: 'DONE' })
  }

  dismiss(id: string): Reminder {
    return this.patch(id, { status: 'DISMISSED' })
  }

  levelForDays(days: number): ReminderLevel | null {
    if (days >= 7) return 'urgent'
    if (days >= 5) return 'warning'
    if (days >= 3) return 'info'
    return null
  }

  private messageFor(opportunity: Opportunity, days: number, level: ReminderLevel): string {
    const name = opportunity.customerName ?? 'cliente'
    if (level === 'urgent') {
      return `URGENTE: ${opportunity.quoteCode} · ${name} parado há ${days} dias`
    }
    if (level === 'warning') {
      return `Atenção: ${opportunity.quoteCode} · ${name} sem contato há ${days} dias`
    }
    return `Lembrete: ${opportunity.quoteCode} · ${name} aguardando há ${days} dias`
  }

  private patch(id: string, patch: Partial<Reminder>): Reminder {
    const all = this.list()
    const index = all.findIndex((r) => r.id === id)
    if (index < 0) throw new Error('Lembrete não encontrado')
    const next = { ...all[index]!, ...patch }
    all[index] = next
    writeCrmJson(CRM_KEYS.reminders, 'reminders', all)
    return next
  }
}

export const reminderService = new ReminderService()

/** Helper for scheduling next contact dates (architecture for calendar integrations). */
export function suggestNextContact(from = nowIso(), days = 3): string {
  return addDaysIso(from, days)
}
